import { useState } from "react";
import { toast } from "sonner";
import { PlusIcon, LinkSimpleIcon, StackIcon } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

export default function CreateTagDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [mode, setMode] = useState("direct");
  const [dest, setDest] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setLabel(""); setMode("direct"); setDest(""); };

  const submit = async () => {
    if (!label.trim()) return toast.error("Give your code a label");
    if (mode === "direct" && !dest.trim()) return toast.error("Direct mode needs a destination URL");
    setBusy(true);
    try {
      const { data } = await api.post("/tags", {
        label: label.trim(),
        mode,
        destination_url: mode === "direct" ? dest.trim() : null,
      });
      toast.success("Code created", { description: `Permanent code: ${data.code}` });
      setOpen(false);
      reset();
      onCreated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create code");
    } finally {
      setBusy(false);
    }
  };

  const ModeCard = ({ id, icon: Icon, title, desc }) => (
    <button
      type="button"
      data-testid={`create-mode-${id}`}
      onClick={() => setMode(id)}
      className={`text-left p-4 border transition-colors ${
        mode === id ? "border-primary bg-primary/5" : "border-border hover:border-white/30"
      }`}
    >
      <Icon weight="duotone" size={22} className={mode === id ? "text-primary" : "text-white/60"} />
      <p className="font-heading font-bold mt-2">{title}</p>
      <p className="text-xs text-white/50 mt-1 leading-relaxed">{desc}</p>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="create-tag-open-btn" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors">
          <PlusIcon weight="bold" className="mr-1.5" /> NEW CODE
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-2xl tracking-tight">Create a new code</DialogTitle>
          <DialogDescription className="text-white/50">A permanent code is generated automatically. You can rewire its destination anytime.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Label</label>
            <Input
              data-testid="create-label-input"
              placeholder="Summer 2026 enamel pin"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="bg-secondary/40 border-border focus-visible:ring-primary"
            />
          </div>
          <div className="space-y-2">
            <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Mode</label>
            <div className="grid grid-cols-2 gap-3">
              <ModeCard id="direct" icon={LinkSimpleIcon} title="Direct" desc="Instant 302 redirect to one URL." />
              <ModeCard id="hub" icon={StackIcon} title="Hub" desc="A branded page of many links. (Rendering coming soon)" />
            </div>
          </div>
          {mode === "direct" && (
            <div className="space-y-2">
              <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Destination URL</label>
              <Input
                data-testid="create-destination-input"
                placeholder="https://your-store.com/product"
                value={dest}
                onChange={(e) => setDest(e.target.value)}
                className="bg-secondary/40 border-border font-mono text-sm focus-visible:ring-primary"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            data-testid="create-tag-submit-btn"
            onClick={submit}
            disabled={busy}
            className="bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors"
          >
            {busy ? "CREATING…" : "CREATE CODE"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
